import type { Rule } from "antd/lib/form";
import type { PropsWithChildren } from "react";

import type { GenericObject } from "../../../core/types";

export type ErrorsFields = ReadonlyArray<{ name: Array<string | number>; errors: Array<string> }>;
export type FormError = Readonly<{ errorFields: ErrorsFields }>;

export interface AntCustomFormField<T> {
    value?: T;
    onChange?: (value: T) => void;
}

export interface FormInputProps<T> extends PropsWithChildren {
    name?: string; // The name of the field
    className?: string; // Default is ""
    disabled?: boolean; // Default is false
    label?: string; // Optional, if not set label is not displayed.
    value?: T; // Optional, if set it's the value of this field.
    onChange?: (value: T) => void; // When the value changes this method is called.
}

export interface FormFieldFactoryProps {
    field: FormField;
    children?: React.JSX.Element;
    readOnlyField?: FormField;
    rules?: Rule[];
    readOnly: boolean;
    className?: string;
    changeCallback?: Function;
    translationKeys?: TranslationKeys;
    component?: string;
}

export interface FormFieldWrapperProps {
    field?: FormFieldFactoryProps;
    formComponent: React.JSX.Element;
}

export type FieldMethod = (props: FormFieldFactoryProps) => React.JSX.Element;

export type QueryParams = Readonly<{
    resource: string;
    resourceName?: string;
    dataGroupId?: string;
    entityId?: string;
    data?: object;
}>;

export type GenericEntityObject = Readonly<{
    genericFormData: GenericObject;
    query: QueryParams;
}>;

export enum ValidationType {
    VALIDATION_REQUIRED = "Required",
    VALIDATION_MAX_LENGTH = "MaxLength",
    VALIDATION_MIN_LENGTH = "MinLength",
    VALIDATION_MIN = "Min",
    VALIDATION_MAX = "Max",
    PATTERN = "Pattern",
}

export type ValidationRuleParams = (string | number | boolean | object)[];
export type ValidationRule = {
    name: ValidationType;
    params?: ValidationRuleParams;
};

export type FormConfiguration = Readonly<{
    resource: string;
    useDataGroup: boolean;
    tabs: ReadonlyArray<FormTab>;
    isSubEntity?: boolean;
}>;

export type FormTab = Readonly<{
    name: string;
    fields: ReadonlyArray<FormField>;
}>;

export type FormField = CommonFormField | SelectDynamicFormField | SelectProfileFormField;

export type Attributes = Readonly<
    | CommonFieldAttributes
    | SelectFieldAttributes
    | TimePickerAttributes
    | DatePickerAttributes
    | ConditionalFieldAttributes
    | AutocompleteFieldAttributes
    | DynamicPropertiesFieldAttributes
>;

export type CommonFormField = Readonly<{
    name: string;
    component: FormComponent;
    translationKeys: TranslationKeys;
    attributes?: Attributes;
    disableIf?: string;
    reference?: string;
    updateFields?: string[];
    rules?: Rule[];
}>;

export interface CommonFieldAttributes {
    readOnly?: boolean;
    readOnlyName?: string;
    validationRules?: ValidationRule[];
    condition?: string;
    watchedFields?: string[];
    defaultValue?: boolean | string | number | object | object[];
    visible?: boolean;
}

export interface SelectDynamicFormField extends CommonFormField {
    urlToFetchOptions: string;
    optionDisplayPropName: string;
    optionValuePropName: string;
    useDataGroup?: boolean;
    queryParams?: { [key: string]: string }[];
}

export interface MultiSelectDynamicFormField extends SelectDynamicFormField {}

export interface SelectProfileFormField extends SelectDynamicFormField {
    updateFields: string[];
}

export interface AutocompleteFieldAttributes extends CommonFieldAttributes {
    datasource: string;
    displayValue: string;
}

export interface ConditionalFieldAttributes extends CommonFieldAttributes {
    watchedFields?: string[];
    condition?: string;
}

export interface SelectFieldAttributes extends CommonFieldAttributes {
    options?: ReadonlyArray<string>;
}

export interface TimePickerAttributes extends CommonFieldAttributes {
    format?: "hh:mm:ss" | "hh:mm";
}

export interface DatePickerAttributes extends CommonFieldAttributes {
    format?: "YYYY-MM-dd'T'HHmmss.SSS+Z" | "YYYY-MM-DDTHH:mm";
    type?: "date" | "month" | "year";
}

export interface HiddenAttributes extends CommonFieldAttributes {
    defaultValue?: boolean;
}

export interface DynamicPropertiesFieldAttributes extends CommonFieldAttributes {
    useReadOnly?: boolean;
}

export type TranslationKeys = Readonly<{
    label: string;
    options?: OptionsTranslations;
}>;

export type OptionsTranslations = ReadonlyArray<{
    key: string;
    label: string;
}>;

export interface PropertyTableSelector {
    onSelected?: Function;
}

export enum FormComponent {
    ACCEPT_TYPE_SELECTOR = "AcceptTypeSelector",
    ACQUIRERS_CONTRACTS_TABLE = "AcquirerContractsTable",
    ACQUIRERS_OPERATORS_TABLE = "AcquirersOperatorsTable",
    APPLICATIONS_PROFILE_CARD_DEFINITIONS_TABLE = "AppProfilesCardDefinitionsTable",
    APPLICATIONS_PROFILE_PROMPT_DATA = "AppProfilePromptDataTable",
    APPLICATION_CONFIGURATIONS_DEFAULT_VALUE_EDIT = "AppConfigsDefaultValueEdit",
    APPLICATION_CONFIGURATIONS_VALIDATIONS_EDIT = "AppConfigsValidationsEdit",
    AUTOCOMPLETE = "Autocomplete",
    TEXT = "Text",
    NUMBER = "Number",
    UPLOAD_FILE = "UploadFile",
    CHECK_BOX = "Checkbox",
    HIDDEN_CHECK_BOX = "HiddenCheckbox",
    READ_ONLY_CHECK = "ReadOnlyCheck",
    SELECT_DYNAMIC = "SelectDynamic",
    MULTI_SELECT_DYNAMIC = "MultiSelectDynamic",
    PROFILE_SELECTOR = "ProfileSelector",
    TEXT_AREA = "Textarea",
    TIME_PICKER = "TimePicker",
    DATE_PICKER = "DatePicker",
    DATA_GROUP_USERS_TABLE = "DataGroupUsersTable",
    DATA_GROUP_IDM_USERS_TABLE = "DataGroupIdmUsersTable",
    TEXT_TEMPLATING = "TextTemplating",
    DYNAMIC_PROPERTY_GROUP_PROPERTIES_TABLE = "DynamicPropertyGroupPropertiesTable",
    INPUT_GENERATOR = "InputGenerator",
    NO_ELEMENT = "",
    SITES_TABLE_VIEW = "SitesTableView",
    VIRTUAL_SITES_TABLE = "VirtualSitesTable",
    FILE_CONTENTS = "FileContents",
    DYNAMIC_PROPERTIES = "DynamicProperties",
    CONTRACTS_TERMINALS_TABLE = "ContractsTerminalsTable",
    CONTRACTS_APP_PROFILES_TABLE = "ContractsAppProfilesTable",
    CONTRACTS_VIRTUAL_APP_PROFILES_TABLE = "ContractsVirtualAppProfilesTable",
    VIRTUAL_ACQUIRERS_PHYSICAL_ACQUIRERS_TABLE = "VirtualPhysicalAcquirersTable",
    SITES_CONTRACTS_TABLE = "SitesContractsTable",
    HIDDEN_INPUT = "HiddenValue",
    PARAMETERS_TABLE = "ParametersTable",
    TERMINAL_ID_GENERATOR = "TerminalIdGenerator",
    TERMINAL_ID_COPY_GENERATOR = "TerminalIdCopyGenerator",
    SITE_ID_GENERATOR = "SiteIdGenerator",
    SITE_ID_COPY_GENERATOR = "SiteIdCopyGenerator",
    TERMINAL_CONTRACTS_TABLE = "TerminalContractsTable",
    TERMINAL_HOST_TERMINAL = "TerminalHostTerminalTable",
    CARD_DEFINITIONS_TABLE = "CardDefinitionsTable",
    CARD_ATTRIBUTES_TABLE = "CardAttributesTable",
    EMV_KERNEL_EMV_CONFIGURATIONS_TABLE = "EmvKernelEmvConfigurationsTable",
    EMV_KERNEL_CA_PUBLIC_KEYS_TABLE = "EmvKernelCaPublicKeysTable",
    EMV_KERNEL_AID_PREFERENCES_TABLE = "EmvKernelAidPreferencesTable",
    EMV_KERNEL_AID_PREFERENCES_CLESS_TABLE = "EmvKernelAidPreferencesClessTable",
    CAMPAIGN_TERMINALS_SELECTABLE_TABLE = "CampaignTerminalsSelectableTable",
    TERMINAL_TO_CAMPAIGN_TABLE = "TerminalToCampaignTable",
    DISPLAY_TEXT = "DisplayText",
    VAT_CODE_VAT_VALUES_TABLE = "VatCodeVatValuesTable",
    CARD_COMBINATIONS = "CardCombinationsTable",
    CARD_TYPES_MAPPING_TABLE = "CardTypesMappingTable",
    PRODUCT_PROFILE_PRODUCTS = "ProductsTable",
    CAMPAIGN_TERMINALS_VIEW_TABLE = "CampaignTerminalsViewTable",
    TERMINALS_TEXTAREA = "TerminalsTextarea",
    SITES_VIRTUAL_SITES_TABLE = "SitesVirtualSitesTable",
    ORG_UNITS_PRODUCTS_PROFILE = "OrgUnitsProductsProfile",
}

export type SearchFormProps<T> = {
    searchCallback?: (filters: T) => void;
};
